/**
 * Nosto-ProductResultsRenderer.js
 *
 * Administra el renderizado de resultados de producto obtenidos desde Nosto.
 * Crea placeholders, limpia o agrega resultados, muestra estados vacíos o de
 * error e invoca una fábrica de ProductCard para renderizar cada producto.
 *
 * Este archivo puede utilizarse tanto en PLP como en Search. No ejecuta
 * búsquedas, no administra filtros, no realiza tracking y no contiene lógica
 * específica de ningún controller.
 */

(function initializeNostoProductResultsRenderer(global) {
    "use strict";

    class NostoProductResultsRenderer {
        /**
         * Crea una instancia del renderer con su contenedor y fábrica de product cards.
         */
        constructor({
            container,
            productCardFactory = null,
            placeholderTagName = "li",
            statusTagName = "",
            placeholderClass = "nosto-product-placeholder",
            productItemClass = "nosto-product-result",
            emptyClass = "nosto-results-empty",
            errorClass = "nosto-results-error",
            loadingClass = "nosto-results-loading",
            emptyMessage = "No se encontraron productos.",
            errorMessage = "No fue posible cargar los productos.",
            onRenderComplete = null,
            debug = false,
        } = {}) {
            this.container = this.resolveElement(container);

            this.productCardFactory =
                productCardFactory ||
                this.createDefaultProductCardFactory();

            this.placeholderTagName =
                this.normalizeTagName(placeholderTagName);

            this.statusTagName =
                this.normalizeTagName(
                    statusTagName ||
                    this.placeholderTagName
                );

            this.placeholderClass =
                this.normalizeString(placeholderClass);

            this.productItemClass =
                this.normalizeString(productItemClass);

            this.emptyClass =
                this.normalizeString(emptyClass);

            this.errorClass =
                this.normalizeString(errorClass);

            this.loadingClass =
                this.normalizeString(loadingClass);

            this.emptyMessage =
                this.normalizeString(emptyMessage);

            this.errorMessage =
                this.normalizeString(errorMessage);

            this.onRenderComplete =
                typeof onRenderComplete ===
                    "function"
                    ? onRenderComplete
                    : null;

            this.debug = Boolean(debug);

            this.renderSequence = 0;
            this.activeRenderId = 0;
            this.renderedProductIds = new Set();
            this.placeholdersByProductId = new Map();
            this.productCardsByProductId = new Map();
        }

        /**
         * Escribe información en consola cuando el modo debug está activo.
         */
        log(...args) {
            if (!this.debug) return;

            console.info(
                "[NostoProductResultsRenderer]",
                ...args
            );
        }

        /**
         * Convierte un valor en una cadena limpia.
         */
        normalizeString(value) {
            return String(value ?? "").trim();
        }

        /**
         * Normaliza el nombre de una etiqueta HTML para crear placeholders seguros.
         */
        normalizeTagName(tagName) {
            const normalizedTagName = this
                .normalizeString(tagName)
                .toLowerCase();

            const validTagNamePattern = /^[a-z][a-z0-9-]*$/;

            return validTagNamePattern.test(normalizedTagName)
                ? normalizedTagName
                : "div";
        }

        /**
         * Busca un elemento a partir de un selector o devuelve directamente el nodo recibido.
         */
        resolveElement(target) {
            if (target instanceof Element) {
                return target;
            }

            if (typeof target === "string") {
                return document.querySelector(target);
            }

            return null;
        }

        /**
         * Comprueba que el contenedor principal exista antes de renderizar.
         */
        ensureContainer() {
            if (this.container instanceof Element) {
                return this.container;
            }

            throw new Error(
                "NostoProductResultsRenderer requiere un contenedor válido."
            );
        }

        /**
         * Reemplaza el contenedor utilizado por el renderer.
         */
        setContainer(container) {
            const resolvedContainer =
                this.resolveElement(container);

            if (!resolvedContainer) {
                throw new Error(
                    "No fue posible establecer el contenedor de resultados."
                );
            }

            this.container = resolvedContainer;

            return this.container;
        }

        /**
         * Crea una fábrica predeterminada utilizando una clase global de ProductCard.
         */
        createDefaultProductCardFactory() {
            return async (options) => {
                const ProductCardClass =
                    global.NostoProductCard ||
                    global.ProductCard;

                if (
                    typeof ProductCardClass !== "function"
                ) {
                    throw new Error(
                        "No se encontró NostoProductCard ni ProductCard. Proporciona productCardFactory al renderer."
                    );
                }

                const productCard = new ProductCardClass(
                    options
                );

                if (
                    productCard &&
                    typeof productCard.render === "function"
                ) {
                    return productCard.render();
                }

                if (
                    productCard &&
                    typeof productCard.create === "function"
                ) {
                    return productCard.create();
                }

                return productCard;
            };
        }

        /**
         * Reemplaza la fábrica encargada de crear product cards.
         */
        setProductCardFactory(productCardFactory) {
            if (
                typeof productCardFactory !== "function"
            ) {
                throw new TypeError(
                    "productCardFactory debe ser una función."
                );
            }

            this.productCardFactory =
                productCardFactory;
        }

        /**
         * Extrae el identificador de producto desde un hit de Nosto.
         */
        getProductId(hit) {
            if (!hit || typeof hit !== "object") {
                return "";
            }

            const productId =
                hit.productId ??
                hit.product_id ??
                hit.id ??
                "";

            return this.extractNumericProductId(
                productId
            );
        }

        /**
         * Extrae el segmento final de un ID numérico o Global ID de Shopify.
         */
        extractNumericProductId(productId) {
            const normalizedProductId =
                this.normalizeString(productId);

            if (!normalizedProductId) {
                return "";
            }

            if (normalizedProductId.includes("/")) {
                return normalizedProductId
                    .split("/")
                    .filter(Boolean)
                    .at(-1);
            }

            return normalizedProductId;
        }

        /**
         * Convierte una cadena en un valor seguro para utilizarlo dentro de un ID HTML.
         */
        sanitizeIdPart(value) {
            return this
                .normalizeString(value)
                .replace(/[^a-zA-Z0-9_-]/g, "-");
        }

        /**
         * Genera un identificador único para un placeholder de producto.
         */
        createPlaceholderId({
            productId,
            renderId,
            index,
        }) {
            const safeProductId =
                this.sanitizeIdPart(productId);

            return [
                "nosto-product",
                safeProductId || "unknown",
                renderId,
                index,
            ].join("-");
        }

        /**
         * Crea un nuevo identificador para una operación completa de renderizado.
         */
        createRenderId() {
            this.renderSequence += 1;
            this.activeRenderId =
                this.renderSequence;

            return this.activeRenderId;
        }

        /**
         * Comprueba si una operación de renderizado sigue siendo la más reciente.
         */
        isRenderActive(renderId) {
            return (
                Number(renderId) ===
                Number(this.activeRenderId)
            );
        }

        /**
         * Invalida cualquier operación de renderizado que siga pendiente.
         */
        cancelPendingRender() {
            this.activeRenderId =
                ++this.renderSequence;

            this.log(
                "Se invalidaron operaciones de renderizado anteriores.",
                this.activeRenderId
            );

            return this.activeRenderId;
        }

        /**
         * Elimina todos los resultados y estados visuales del contenedor.
         */
        clear() {
            const container =
                this.ensureContainer();

            this.cancelPendingRender();

            container.replaceChildren();

            this.renderedProductIds.clear();
            this.placeholdersByProductId.clear();
            this.destroyProductCards();

            container.classList.remove(
                this.loadingClass
            );

            this.log(
                "El contenedor de resultados fue limpiado."
            );
        }

        /**
         * Elimina únicamente los estados vacíos o de error existentes.
         */
        clearStatusMessages() {
            const container =
                this.ensureContainer();

            container
                .querySelectorAll(
                    [
                        `.${this.emptyClass}`,
                        `.${this.errorClass}`,
                    ].join(",")
                )
                .forEach((element) =>
                    element.remove()
                );
        }

        /**
 * Activa o desactiva el estado accesible de carga.
 */
        setLoading(isLoading = true) {
            const container =
                this.ensureContainer();

            const loading =
                Boolean(isLoading);

            container.classList.toggle(
                this.loadingClass,
                loading
            );

            container.setAttribute(
                "aria-busy",
                String(loading)
            );

            container.dataset.nostoLoading =
                String(loading);
        }

        /**
         * Crea el elemento base que será reemplazado por un product card.
         */
        createPlaceholder({
            hit,
            productId,
            index,
            renderId,
            context = {},
        }) {
            const placeholder =
                document.createElement(
                    this.placeholderTagName
                );

            const placeholderId =
                this.createPlaceholderId({
                    productId,
                    renderId,
                    index,
                });

            placeholder.id = placeholderId;

            if (this.placeholderClass) {
                placeholder.classList.add(
                    this.placeholderClass
                );
            }

            if (this.productItemClass) {
                placeholder.classList.add(
                    this.productItemClass
                );
            }

            placeholder.dataset.nostoProductId =
                productId;

            placeholder.dataset.nostoIndex =
                String(index);

            placeholder.dataset.nostoRenderId =
                String(renderId);

            if (context.source) {
                placeholder.dataset.nostoSource =
                    String(context.source);
            }

            if (context.track) {
                placeholder.dataset.nostoTrack =
                    String(context.track);
            }

            placeholder.setAttribute(
                "aria-busy",
                "true"
            );

            return placeholder;
        }

        /**
         * Crea todos los placeholders de una lista de hits sin insertarlos todavía.
         */
        createPlaceholders(
            hits = [],
            {
                renderId,
                context = {},
                skipDuplicates = true,
            } = {}
        ) {
            if (!Array.isArray(hits)) {
                return [];
            }

            const placeholders = [];

            hits.forEach((hit, index) => {
                const productId =
                    this.getProductId(hit);

                if (!productId) {
                    this.log(
                        "Se ignoró un hit sin productId.",
                        hit
                    );

                    return;
                }

                if (
                    skipDuplicates &&
                    this.renderedProductIds.has(productId)
                ) {
                    this.log(
                        "Se ignoró un producto duplicado.",
                        productId
                    );

                    return;
                }

                const placeholder =
                    this.createPlaceholder({
                        hit,
                        productId,
                        index,
                        renderId,
                        context,
                    });

                placeholders.push({
                    hit,
                    productId,
                    index,
                    placeholder,
                    placeholderId: placeholder.id,
                });
            });

            return placeholders;
        }

        /**
         * Inserta varios placeholders en el contenedor usando un DocumentFragment.
         */
        appendPlaceholders(
            placeholders = []
        ) {
            const container =
                this.ensureContainer();

            const fragment =
                document.createDocumentFragment();

            for (const item of placeholders) {
                fragment.appendChild(
                    item.placeholder
                );

                this.renderedProductIds.add(
                    item.productId
                );

                this.placeholdersByProductId.set(
                    item.productId,
                    item.placeholder
                );
            }

            container.appendChild(fragment);

            return placeholders;
        }

        /**
         * Obtiene el placeholder registrado para un producto.
         */
        getPlaceholder(productId) {
            const normalizedProductId =
                this.extractNumericProductId(
                    productId
                );

            return (
                this.placeholdersByProductId.get(
                    normalizedProductId
                ) || null
            );
        }

        /**
         * Elimina un placeholder y libera su registro interno.
         */
        removePlaceholder(productId) {
            const normalizedProductId =
                this.extractNumericProductId(
                    productId
                );

            const placeholder =
                this.placeholdersByProductId.get(
                    normalizedProductId
                );

            if (placeholder?.isConnected) {
                placeholder.remove();
            }

            this.placeholdersByProductId.delete(
                normalizedProductId
            );

            this.renderedProductIds.delete(
                normalizedProductId
            );

            const productCard =
                this.productCardsByProductId.get(
                    normalizedProductId
                );

            productCard?.destroy?.();

            this.productCardsByProductId.delete(
                normalizedProductId
            );

            return Boolean(placeholder);
        }

        /**
         * Invoca la fábrica de ProductCard para renderizar un producto específico.
         */
        async renderProductCard({
            hit,
            productId,
            placeholder,
            placeholderId,
            index,
            renderId,
            context = {},
        }) {
            if (!this.isRenderActive(renderId)) {
                return {
                    status: "cancelled",
                    productId,
                };
            }

            try {
                const result =
                    await this.productCardFactory({
                        hit,
                        productId,
                        container: placeholder,
                        containerId: placeholderId,
                        index,
                        renderId,
                        context,
                        source: context.source || "",
                        track: context.track || "",
                        attributionKey:
                            context.attributionKey || "",
                    });

                if (!this.isRenderActive(renderId)) {
                    return {
                        status: "cancelled",
                        productId,
                    };
                }

                if (placeholder?.isConnected) {
                    placeholder.setAttribute(
                        "aria-busy",
                        "false"
                    );
                }

                /**
                 * Elimina el estado visual del placeholder después de renderizar la card.
                 */
                placeholder.classList.remove(
                    this.placeholderClass
                );

                placeholder.classList.add(
                    "nosto-product-result--rendered"
                );

                placeholder.removeAttribute(
                    "aria-busy"
                );

                placeholder.dataset.nostoRendered =
                    "true";

                if (
                    result &&
                    typeof result ===
                    "object"
                ) {
                    this.productCardsByProductId.set(
                        productId,
                        result
                    );
                }

                return {
                    status: "fulfilled",
                    productId,
                    result,
                };
            } catch (error) {
                if (placeholder?.isConnected) {
                    placeholder.remove();
                }

                this.placeholdersByProductId.delete(
                    productId
                );

                this.renderedProductIds.delete(
                    productId
                );

                this.productCardsByProductId.delete(
                    productId
                );

                console.error(
                    `[NostoProductResultsRenderer] No fue posible renderizar el producto ${productId}.`,
                    error
                );

                return {
                    status: "rejected",
                    productId,
                    error,
                };
            }
        }

        /**
         * Renderiza todos los product cards asociados a una lista de placeholders.
         */
        async renderProductCards(
            placeholders = [],
            {
                renderId,
                context = {},
            } = {}
        ) {
            const operations = placeholders.map(
                (item) =>
                    this.renderProductCard({
                        ...item,
                        renderId,
                        context,
                    })
            );

            return Promise.all(operations);
        }

        /**
         * Muestra un mensaje de estado dentro del contenedor.
         */
        renderMessage({
            message,
            className,
            attributes = {},
            replace = true,
        } = {}) {
            const container =
                this.ensureContainer();

            if (replace) {
                container.replaceChildren();
                this.renderedProductIds.clear();
                this.placeholdersByProductId.clear();
            }

            const messageElement =
                document.createElement(
                    this.statusTagName
                );

            messageElement.dataset
                .nostoResultsStatus = "";

            if (className) {
                messageElement.className =
                    className;
            }

            messageElement.textContent =
                this.normalizeString(message);

            for (const [name, value] of Object.entries(
                attributes
            )) {
                if (value == null) continue;

                messageElement.setAttribute(
                    name,
                    String(value)
                );
            }

            container.appendChild(
                messageElement
            );

            return messageElement;
        }

        /**
 * Muestra el estado visual cuando no existen productos.
 */
        renderEmpty(
            message = this.emptyMessage,
            {
                replace = true,
            } = {}
        ) {
            this.setLoading(false);

            return this.renderMessage({
                message,
                className:
                    this.emptyClass,
                replace,

                attributes: {
                    role: "status",
                    "aria-live": "polite",
                    "data-nosto-results-empty": "",
                },
            });
        }

        /**
 * Muestra el estado visual cuando ocurre un error general.
 */
        renderError(
            error = null,
            {
                message = this.errorMessage,
                replace = true,
                retryable = true,
            } = {}
        ) {
            this.setLoading(false);

            if (error) {
                console.error(
                    "[NostoProductResultsRenderer]",
                    error
                );
            }

            return this.renderMessage({
                message,
                className:
                    this.errorClass,
                replace,

                attributes: {
                    role: "alert",
                    "aria-live": "assertive",
                    "data-nosto-results-error": "",
                    "data-nosto-retryable":
                        String(
                            Boolean(retryable)
                        ),
                },
            });
        }

        /**
 * Muestra un error sin eliminar los productos existentes.
 */
        renderAppendError(
            error = null,
            message =
                "No fue posible cargar más productos."
        ) {
            this.clearStatusMessages();

            return this.renderError(
                error,
                {
                    message,
                    replace: false,
                    retryable: true,
                }
            );
        }

        /**
         * Renderiza una lista de hits reemplazando o agregando resultados existentes.
         */
        async render(
            hits = [],
            {
                mode = "replace",
                context = {},
                skipDuplicates = true,
                showEmpty = true,
            } = {}
        ) {
            const container =
                this.ensureContainer();

            const safeHits =
                Array.isArray(hits)
                    ? hits.filter(Boolean)
                    : [];

            const normalizedMode =
                mode === "append"
                    ? "append"
                    : "replace";

            const renderId =
                this.createRenderId();

            if (normalizedMode === "replace") {
                this.destroyProductCards();

                container.replaceChildren();

                this.renderedProductIds.clear();
                this.placeholdersByProductId.clear();
            } else {
                this.clearStatusMessages();
            }

            if (
                safeHits.length === 0 &&
                normalizedMode === "replace"
            ) {
                this.setLoading(false);

                if (showEmpty) {
                    this.renderEmpty(
                        context.emptyMessage ||
                        this.emptyMessage
                    );
                }

                const summary = {
                    renderId,
                    mode: normalizedMode,
                    total: 0,
                    rendered: 0,
                    failed: 0,
                    cancelled: 0,
                    results: [],
                };

                this.onRenderComplete?.(
                    summary
                );

                return summary;
            }

            this.setLoading(
                normalizedMode === "replace"
            );

            const placeholders =
                this.createPlaceholders(safeHits, {
                    renderId,
                    context,
                    skipDuplicates,
                });

            this.appendPlaceholders(
                placeholders
            );

            const results =
                await this.renderProductCards(
                    placeholders,
                    {
                        renderId,
                        context,
                    }
                );

            if (this.isRenderActive(renderId)) {
                this.setLoading(false);
            }

            const summary = {
                renderId,
                mode: normalizedMode,
                total: placeholders.length,
                rendered: results.filter(
                    (result) =>
                        result.status === "fulfilled"
                ).length,
                failed: results.filter(
                    (result) =>
                        result.status === "rejected"
                ).length,
                cancelled: results.filter(
                    (result) =>
                        result.status === "cancelled"
                ).length,
                results,
            };

            if (
                this.isRenderActive(
                    renderId
                ) &&
                placeholders.length > 0 &&
                summary.rendered === 0 &&
                summary.failed > 0
            ) {
                this.renderError(
                    summary.results[0]
                        ?.error ||
                    null,
                    {
                        message:
                            "No fue posible mostrar los productos.",
                        replace:
                            normalizedMode ===
                            "replace",
                    }
                );
            }

            this.log(
                "Renderizado finalizado.",
                summary
            );

            this.onRenderComplete?.(
                summary
            );

            return summary;
        }

        /**
         * Reemplaza los resultados existentes por una nueva lista de hits.
         */
        async replace(
            hits = [],
            options = {}
        ) {
            return this.render(hits, {
                ...options,
                mode: "replace",
            });
        }

        /**
         * Agrega nuevos resultados al final de los productos existentes.
         */
        async append(
            hits = [],
            options = {}
        ) {
            return this.render(hits, {
                ...options,
                mode: "append",
            });
        }

        /**
         * Cambia el tipo visual de todas las cards ya renderizadas.
         */
        setCardTypeForAll(cardType) {
            let updatedCount = 0;

            for (
                const productCard of
                this.productCardsByProductId.values()
            ) {
                if (
                    typeof productCard
                        ?.setCardType !==
                    "function"
                ) {
                    continue;
                }

                productCard.setCardType(
                    cardType
                );

                updatedCount += 1;
            }

            return updatedCount;
        }

        /**
         * Destruye las instancias de cards registradas.
         */
        destroyProductCards() {
            for (
                const productCard of
                this.productCardsByProductId.values()
            ) {
                productCard?.destroy?.();
            }

            this.productCardsByProductId.clear();
        }

        /**
         * Devuelve la cantidad de productos registrados como renderizados.
         */
        getRenderedCount() {
            return this.renderedProductIds.size;
        }

        /**
         * Comprueba si un producto ya fue agregado al contenedor.
         */
        hasRenderedProduct(productId) {
            const normalizedProductId =
                this.extractNumericProductId(
                    productId
                );

            return this.renderedProductIds.has(
                normalizedProductId
            );
        }
    }

    global.NostoProductResultsRenderer =
        NostoProductResultsRenderer;
})(window);